import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Hook that enables drag-to-zoom on Chart.js charts rendered via react-chartjs-2.
// Provides the current zoom range so parent components can feed it into scale options.
export default function useChartDragZoom(chartRef, options = {}) {
    const { minimumSelectionWidth = 8, enabled = true } = options;

    const selectionRef = useRef({
        dragging: false,
        start: null,
        end: null,
    });

    const [zoomRange, setZoomRange] = useState(null);

    const resetZoom = useCallback(() => {
        setZoomRange(null);
    }, []);

    useEffect(() => {
        let rafId;
        let cleanup = () => {};
        let attachedCanvas = null;

        if (!enabled) {
            setZoomRange(null);
            selectionRef.current.dragging = false;
            selectionRef.current.start = null;
            selectionRef.current.end = null;
            return () => {
                if (rafId) cancelAnimationFrame(rafId);
                cleanup();
            };
        }

        const attachListeners = (chart, canvas) => {
            const pointerState = selectionRef.current;

            const getRelativePosition = (event) => {
                const rect = canvas.getBoundingClientRect();
                return {
                    x: event.clientX - rect.left,
                    y: event.clientY - rect.top,
                };
            };

            const clampX = (pixel, area) =>
                Math.max(area.left, Math.min(area.right, pixel));

            const handlePointerDown = (event) => {
                if (event.button !== 0) return;

                const area = chart.chartArea;
                if (!area) return;

                const { x, y } = getRelativePosition(event);
                if (x < area.left || x > area.right) return;
                if (y < area.top || y > area.bottom) return;

                pointerState.dragging = true;
                const start = clampX(x, area);
                pointerState.start = start;
                pointerState.end = start;

                chart.draw();

                try {
                    canvas.setPointerCapture?.(event.pointerId);
                } catch (err) {
                    // Ignore environments where pointer capture is unsupported.
                }
            };

            const handlePointerMove = (event) => {
                if (!pointerState.dragging) return;

                const area = chart.chartArea;
                if (!area) return;

                const { x } = getRelativePosition(event);
                pointerState.end = clampX(x, area);
                chart.draw();
                event.preventDefault();
            };

            const finalizeSelection = (event) => {
                if (!pointerState.dragging) return;

                pointerState.dragging = false;

                try {
                    canvas.releasePointerCapture?.(event.pointerId);
                } catch (err) {
                    // Safe to ignore if the capture was never set.
                }

                const start = pointerState.start;
                const end = pointerState.end;

                pointerState.start = null;
                pointerState.end = null;

                chart.draw();

                if (start == null || end == null) return;

                const area = chart.chartArea;
                if (!area) return;

                const clampedLeft = Math.max(
                    area.left,
                    Math.min(area.right, Math.min(start, end))
                );
                const clampedRight = Math.max(
                    area.left,
                    Math.min(area.right, Math.max(start, end))
                );
                const width = clampedRight - clampedLeft;
                if (width <= 0 || width < minimumSelectionWidth) return;

                const xScale = chart.scales?.x;
                if (!xScale) return;

                const minValue = xScale.getValueForPixel(clampedLeft);
                const maxValue = xScale.getValueForPixel(clampedRight);
                if (
                    minValue == null ||
                    maxValue == null ||
                    Number.isNaN(minValue) ||
                    Number.isNaN(maxValue) ||
                    minValue === maxValue
                ) {
                    return;
                }

                setZoomRange({
                    min: Math.min(minValue, maxValue),
                    max: Math.max(minValue, maxValue),
                });
            };

            const handlePointerUp = (event) => {
                finalizeSelection(event);
            };

            const handlePointerLeave = (event) => {
                finalizeSelection(event);
            };

            const previousCursor = canvas.style.cursor;
            canvas.style.cursor = 'crosshair';

            canvas.addEventListener('pointerdown', handlePointerDown);
            canvas.addEventListener('pointermove', handlePointerMove);
            canvas.addEventListener('pointerup', handlePointerUp);
            canvas.addEventListener('pointerleave', handlePointerLeave);
            canvas.addEventListener('pointercancel', handlePointerLeave);

            cleanup = () => {
                canvas.style.cursor = previousCursor;
                canvas.removeEventListener('pointerdown', handlePointerDown);
                canvas.removeEventListener('pointermove', handlePointerMove);
                canvas.removeEventListener('pointerup', handlePointerUp);
                canvas.removeEventListener('pointerleave', handlePointerLeave);
                canvas.removeEventListener('pointercancel', handlePointerLeave);
            };
        };

        const syncAttachment = () => {
            const chart = chartRef.current;
            const canvas = chart?.canvas || null;

            if (canvas !== attachedCanvas) {
                cleanup();
                cleanup = () => {};
                attachedCanvas = canvas;
                if (chart && canvas) {
                    attachListeners(chart, canvas);
                }
            }

            rafId = requestAnimationFrame(syncAttachment);
        };

        syncAttachment();

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            cleanup();
        };
    }, [chartRef, minimumSelectionWidth, enabled]);

    const selectionOverlayPlugin = useMemo(
        () => ({
            id: 'chartDragZoomOverlay',
            afterDatasetsDraw: (chart) => {
                if (!enabled) return;
                const selection = selectionRef.current;
                if (!selection.dragging) return;
                if (selection.start == null || selection.end == null) return;

                const area = chart.chartArea;
                if (!area) return;

                const left = Math.max(
                    Math.min(selection.start, selection.end),
                    area.left
                );
                const right = Math.min(
                    Math.max(selection.start, selection.end),
                    area.right
                );
                if (right <= left) return;

                const ctx = chart.ctx;
                ctx.save();
                ctx.fillStyle = 'rgba(37, 99, 235, 0.15)';
                ctx.strokeStyle = 'rgba(37, 99, 235, 0.45)';
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.rect(left, area.top, right - left, area.bottom - area.top);
                ctx.fill();
                ctx.stroke();
                ctx.restore();
            },
        }),
        [enabled]
    );

    return {
        zoomRange,
        isZoomed: zoomRange != null,
        resetZoom,
        selectionOverlayPlugin,
    };
}
